import React, { useEffect, useState } from "react";
import "./Footer.css";
import {
  markNewsletterSubscribed,
  subscribeToNewsletter,
} from "../../utils/newsletterSubscription";

// 暂时隐藏 Instagram；需要恢复时改为 true 即可。
const SHOW_INSTAGRAM_LINK = false;

function Footer({ language = "EN" }) {
  const [activeModal, setActiveModal] = useState(null);
  const [subscriberEmail, setSubscriberEmail] = useState("");
  const [subscriptionStatus, setSubscriptionStatus] = useState("idle");
  const [isModalExiting, setIsModalExiting] = useState(false);

  const openModal = (modalName) => {
    setIsModalExiting(false);
    setActiveModal(modalName);
  };

  const closeModal = () => {
    setIsModalExiting(true);
    setTimeout(() => {
      setActiveModal(null);
      setIsModalExiting(false);
    }, 380);
  };

  useEffect(() => {
    if (!(activeModal || isModalExiting)) return;

    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        closeModal();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = "";
    };
  }, [activeModal, isModalExiting]);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    const email = subscriberEmail.trim();
    if (!email) return;

    setSubscriptionStatus("loading");
    try {
      await subscribeToNewsletter({ email, language });
      setSubscriberEmail("");
      setSubscriptionStatus("success");
      markNewsletterSubscribed();
    } catch (error) {
      setSubscriptionStatus(
        error.code === "invalid_email" ? "invalid" : "error"
      );
    }
  };

  const handleSubscriberEmailChange = (event) => {
    setSubscriberEmail(event.target.value);
    if (subscriptionStatus !== "idle" && subscriptionStatus !== "loading") {
      setSubscriptionStatus("idle");
    }
  };

  const copy =
    language === "CN"
      ? {
          project: "非商业用途",
          copyrightPrefix:
            "本站所有图像与资料仅用于教育和非商业用途。如你认为任何内容侵犯了你的版权，请联系",
          copyrightSuffix: "，我们会尽快删除相关内容。",
          emailAria: "邮件联系",
          modalTitle: "联系我们",
          contactText: "如需反馈、合作或版权联系，请发送邮件至",
          emailLabel: "邮箱地址",
          emailPlaceholder: "你的邮箱",
          subscribe: "订阅",
          subscribing: "提交中…",
          subscribeSuccess: "订阅成功。之后有版本更新时，我们会邮件通知你。",
          subscribeInvalid: "请输入有效的邮箱地址。",
          subscribeError: "暂时无法订阅，请稍后再试。",
          close: "关闭",
        }
      : {
          project: "Non-Commercial Use",
          copyrightPrefix:
            "All images and materials are used for educational and non-commercial purposes. If you believe any content infringes your copyright, please contact us at",
          copyrightSuffix: ", and we will promptly remove the material.",
          emailAria: "Email me",
          modalTitle: "Contact Us",
          contactText:
            "For feedback, collaboration, or copyright inquiries, email us at",
          emailLabel: "Email address",
          emailPlaceholder: "Your email",
          subscribe: "Subscribe",
          subscribing: "Subscribing…",
          subscribeSuccess:
            "You’re subscribed. We’ll email you when a new update is released.",
          subscribeInvalid: "Please enter a valid email address.",
          subscribeError: "Unable to subscribe right now. Please try again.",
          close: "Close",
        };

  return (
    <footer className="footer">
      <div className="footer-container">
        <div className="footer-content">
          <div className="footer-line">
            <span className="footer-text">{copy.project}</span>
            <span className="footer-separator">|</span>
            <span className="footer-text">© 2026 ARCHIVE OF LIGHT</span>
          </div>
          <p className="footer-copyright">
            {copy.copyrightPrefix}{" "}
            <a
              href="mailto:archiveoflightcontact@gmail.com"
              className="footer-email-link"
            >
              archiveoflightcontact@gmail.com
            </a>
            {copy.copyrightSuffix}
          </p>
        </div>
        <div className="footer-actions">
          <form className="footer-subscription-form" onSubmit={handleSubscribe}>
            <label className="visually-hidden" htmlFor="footer-subscriber-email">
              {copy.emailLabel}
            </label>
            <div className="footer-subscription-fields">
              <input
                id="footer-subscriber-email"
                className="footer-subscription-input"
                type="email"
                inputMode="email"
                autoComplete="email"
                required
                placeholder={copy.emailPlaceholder}
                value={subscriberEmail}
                onChange={handleSubscriberEmailChange}
                aria-describedby="footer-subscription-status"
              />
              <button
                type="submit"
                className="footer-subscription-submit"
                disabled={subscriptionStatus === "loading"}
              >
                {subscriptionStatus === "loading"
                  ? copy.subscribing
                  : copy.subscribe}
              </button>
            </div>
            <p
              id="footer-subscription-status"
              className={`footer-subscription-status footer-subscription-status-${subscriptionStatus}`}
              aria-live="polite"
            >
              {subscriptionStatus === "success"
                ? copy.subscribeSuccess
                : subscriptionStatus === "invalid"
                ? copy.subscribeInvalid
                : subscriptionStatus === "error"
                ? copy.subscribeError
                : ""}
            </p>
          </form>
          {SHOW_INSTAGRAM_LINK && (
            <a
              href="https://www.instagram.com/zanex2407/"
              target="_blank"
              rel="noopener noreferrer"
              className="footer-icon"
            >
              <i className="fab fa-instagram"></i>
            </a>
          )}
          <button
            type="button"
            className="footer-icon footer-email-button"
            onClick={() => openModal("contact")}
            aria-label={copy.emailAria}
          >
            <i className="fas fa-at"></i>
          </button>
        </div>
      </div>

      {(activeModal || isModalExiting) && (
        <div
          className={`email-modal-overlay ${
            isModalExiting
              ? "email-modal-overlay-exit"
              : "email-modal-overlay-enter"
          }`}
          role="dialog"
          aria-modal="true"
          aria-labelledby="contact-modal-title"
          aria-describedby="contact-modal-desc"
          onClick={closeModal}
        >
          <div
            className={`email-modal ${
              isModalExiting ? "email-modal-exit" : "email-modal-enter"
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="email-modal-header">
              <h3 id="contact-modal-title" className="email-modal-title">
                {copy.modalTitle}
              </h3>
              <button
                type="button"
                className="email-close-button"
                aria-label={copy.close}
                onClick={closeModal}
              >
                <span aria-hidden>×</span>
              </button>
            </div>

            <p id="contact-modal-desc" className="email-modal-desc">
              {copy.contactText}{" "}
              <a
                href="mailto:archiveoflightcontact@gmail.com"
                className="email-modal-address"
              >
                archiveoflightcontact@gmail.com
              </a>
            </p>
          </div>
        </div>
      )}
    </footer>
  );
}

export default Footer;
